import { useToast } from "@chakra-ui/react";
import { getEnv } from "mobx-easy";
import { OperationRuleAlertNotificationType } from "enum/OperationRuleAlertNotificationType";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  IOperationRuleAlertUser,
  AlertChannel,
} from "stores/data/insightAlerts/insightAlertModel";
import { RootEnv } from "stores/setup/create-store";
import { useStores } from "stores/setup/use-store";
import { processObjectsForBackend } from "util/processObjectsForBackend/processObjectsForBackend";
import { DBAction } from "../../../../enum/DBAction";
import { EditAlertFormValues } from "pages/Alert/types";
import { useHistory, useLocation } from "react-router-dom";
import { useAlertEditFormData } from "./useAlertEditFormData";

export function useAlertEditSubmission() {
  const {
    dataStores: { insightAlertStore },
  } = useStores();

  const toast = useToast();
  const { t } = useTranslation("translation", {
    keyPrefix: "oceanWeatherIntelligence.insightAlert",
  });
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const { resetInitialValues } = useAlertEditFormData();

  const handleOnSaveFormAlertModal = useCallback(
    async (
      value: EditAlertFormValues,
      selectedAlertType: string,
      clearUserTableSelected: () => void,
      onClose: () => void
    ) => {
      const alertId = queryParams.get("alert_id");
      if (alertId) {
        history.replace(location.pathname);
      }

      if (insightAlertStore.operationRuleAlertUserSelected.length === 0) {
        toast({
          title: t("selectRecipient"),
          status: "error",
          position: "top",
          isClosable: true,
        });
        return;
      }

      // Check if this is an insight alert (has operationRuleLocationItemId)
      const isInsightAlert = !!insightAlertStore.operationRuleAlertForTableForEdit?.operationRuleLocationItemId;

      // Only validate macroRegionOrigin for standalone alerts, not insight alerts
      if (!isInsightAlert && !insightAlertStore.macroRegionOrigin) {
        toast({
          title: t("noMacroRegionOriginWasFound"),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      let notificationsObject = {
        id: 0,
        title: value?.customTitle,
        description: value?.customDescription,
        minutesType: undefined,
        minAdvanceTime: value?.minAdvanceTime,
        maxAdvanceTime: value?.maxAdvanceTime,
        minimumEventDuration: value?.minimumEventDuration,
        type: OperationRuleAlertNotificationType?.BEFORE,
        action: DBAction.CREATE,
        sendUpdates: value?.receiptType?.includes("sendUpdates"),
        sendCancellations: value?.receiptType?.includes("sendCancellations"),
        startTime: value?.startTime || "00:00",
        endTime: value?.endTime || "23:59",
      };

      if (insightAlertStore.operationRuleAlertAction === DBAction.CREATE) {
        const recipients: IOperationRuleAlertUser[] =
          insightAlertStore.operationRuleAlertUserSelected?.map((u) => ({
            id: 0,
            userId: u.userId,
            userName: u.userName,
            action: DBAction.CREATE,
          }));

        // For insight alerts, use the notification title as the alert name
        // Use trim() check to also handle whitespace-only strings
        const alertName = value?.name?.trim() ? value.name : value?.customTitle;

        insightAlertStore.addOperationRuleAlertAPI({
          id: 0,
          active: true,
          name: alertName,
          recipients,
          notifications: [notificationsObject],
          alertType: selectedAlertType
        });

        clearUserTableSelected();
        insightAlertStore.setOperationRuleDetailSelected(0);
      } else if (
        insightAlertStore.operationRuleAlertAction === DBAction.UPDATE
      ) {
        if (!insightAlertStore.operationRuleAlert) return;

        // Use the ID from operationRuleAlertForTableForEdit as it's set immediately when user clicks edit
        // operationRuleAlert.id may be incorrect if the API hasn't finished loading yet
        const alertIdToUpdate = insightAlertStore.operationRuleAlertForTableForEdit?.id
          ?? insightAlertStore.operationRuleAlert.id;

        const recipientProcessedForBackend = processObjectsForBackend(
          insightAlertStore.operationRuleAlertUserSelectedBeforeEdit,
          insightAlertStore.operationRuleAlertUserSelected,
          "id"
        );
        const notification =
          insightAlertStore.operationRuleAlert?.notifications?.[0];

        const notificationPayload = {
          ...notificationsObject,
          id: notification?.id ?? 0,
          action: notification ? DBAction.UPDATE : DBAction.CREATE,
        };

        const geofence = insightAlertStore.selectedGeofence.length ? insightAlertStore.selectedGeofence : value.geofence;
        const isAreaMode = insightAlertStore.alertLocationType === "area";
        const locationId = insightAlertStore.isGeofenceSavedToLocation || isAreaMode ? value.locationId : undefined;

        let stationId: number | null | undefined;
        let regionId: number | null | undefined;
        let pointGeofence: { lat: number; lng: number }[] | undefined;

        if (isAreaMode) {
          stationId = null;
          regionId = null;
        } else {
          const selectedStation = insightAlertStore.selectedStationForAlert;
          const isCustomPoint = selectedStation?.station_id === 0;

          if (isCustomPoint && selectedStation) {
            pointGeofence = [{ lat: selectedStation.station_lat, lng: selectedStation.station_lon }];
            stationId = undefined;
            regionId = undefined;
          } else if (selectedStation && selectedStation.station_id > 0) {
            // Station selected - clear geofence and use station data
            stationId = selectedStation.station_id;
            regionId = selectedStation.region_id;

            // Clear geofence since we're using a station
            pointGeofence = undefined;
          } else if (value.stationRegion) {
            const [stationStr] = value.stationRegion.split("-");
            const parsed = Number.parseInt(stationStr, 10);
            if (Number.isFinite(parsed) && parsed > 0) {
              stationId = parsed;
              regionId = value.macroRegionId;
            } else {
              stationId = insightAlertStore.operationRuleAlert.stationId;
              regionId = insightAlertStore.operationRuleAlert.regionId;
            }
          } else {
            stationId = insightAlertStore.operationRuleAlert.stationId;
            regionId = insightAlertStore.operationRuleAlert.regionId;
          }
        }

        // Determine final geofence:
        // - For custom points: use pointGeofence (single coordinate)
        // - For stations (stationId > 0): geofence should be undefined
        // - For areas with locationId: geofence should be undefined
        // - For areas without locationId: use the drawn geofence
        let finalGeofence: { lat: number; lng: number }[] | { lat: number; lng: number }[][] | undefined;

        if (pointGeofence) {
          // Custom point
          finalGeofence = pointGeofence;
        } else if (stationId && stationId > 0) {
          // Station selected - no geofence needed
          finalGeofence = undefined;
        } else if (locationId) {
          // Area with saved location - no geofence needed
          finalGeofence = undefined;
        } else {
          // Area with drawn geofence
          finalGeofence = geofence;
        }

        // Determine if this is an insight alert and get the operationRuleLocationItemId
        const isInsightAlert = !!insightAlertStore.operationRuleAlertForTableForEdit?.operationRuleLocationItemId;
        const operationRuleLocationItemId = isInsightAlert
          ? (value.insightId ?? insightAlertStore.operationRuleAlertForTableForEdit?.operationRuleLocationItemId)
          : undefined;

        // For insight alerts, don't send conditionGroups - they come from the insight
        // For standalone alerts, send the conditionGroups from the form values (not from store)
        // This ensures that when user changes alert type, the new template conditions are saved
        const conditionGroups = isInsightAlert
          ? undefined
          : value.conditionGroups;

        // For insight alerts, location fields must be null - only operationRuleLocationItemId is used
        // For standalone alerts, use the locationId from form
        const finalLocationId = isInsightAlert ? undefined : locationId;

        // For insight alerts, don't send geofence - only operationRuleLocationItemId is needed
        const geofenceToSend = isInsightAlert ? undefined : finalGeofence;

        // For insight alerts, stationId and regionId must be null
        const finalStationId = isInsightAlert ? undefined : stationId;
        const finalRegionId = isInsightAlert ? undefined : regionId;

        // Preserve conditions in local state for insight alerts to avoid UI flicker during save
        // Note: conditions are NOT sent to backend for insight alerts - only operationRuleLocationItemId is needed
        const conditionsForLocalState = isInsightAlert
          ? insightAlertStore.operationRuleAlert?.conditions
          : undefined;

        // Preserve station info for insight alerts to avoid UI flicker during save
        const stationForLocalState = isInsightAlert
          ? insightAlertStore.operationRuleAlert?.station
          : undefined;

        // For insight alerts, use the notification title as the alert name
        const alertName = value.name || value?.customTitle;

        // Sensor alert config (3-state):
        // - undefined → backend preserves existing sensor records (no-op)
        // - null → backend deletes existing sensor records
        // - { sensors?, conditions?, alertFrequency?, lightning? } → backend creates/updates
        // Spec 043: lightning slice flows through here too (rain-only, lightning-only, or both).
        const sensorAlertConfig = (() => {
          if (!isInsightAlert || value.sensorAlertConfig === undefined) {
            return undefined;
          }
          const cfg = value.sensorAlertConfig;
          const hasRain = !!cfg?.sensors?.length;
          const hasLightning = !!cfg?.lightning;
          if (!hasRain && !hasLightning) return null;

          // Spec 043 §FR-014a — anchor preservation: forward the loaded anchor verbatim
          // iff the user has not moved the lightning point. Drop it (undefined) on movement
          // so the BE persists null.
          let lightningPayload = cfg.lightning;
          if (hasLightning && cfg.lightning) {
            const loaded =
              insightAlertStore.operationRuleAlert?.sensorAlertConfig?.lightning;
            const pointUnchanged =
              !!loaded &&
              loaded.point.lat === cfg.lightning.point.lat &&
              loaded.point.lng === cfg.lightning.point.lng;
            lightningPayload = {
              ...cfg.lightning,
              anchor:
                pointUnchanged && loaded?.anchor ? loaded.anchor : undefined,
            };
          }

          return {
            sensors: hasRain
              ? cfg.sensors.map((s) => ({
                  stationId: s.stationId,
                  regionId: s.regionId,
                  stationName: s.stationName ?? "",
                }))
              : [],
            conditions: cfg.conditions || [],
            alertFrequency: cfg.alertFrequency,
            lightning: lightningPayload,
          };
        })();

        insightAlertStore.setOperationRuleAlert({
          id: alertIdToUpdate,
          active: true,
          name: alertName,
          recipients: recipientProcessedForBackend,
          notifications: [notificationPayload],
          geofence: geofenceToSend,
          conditionGroups,
          conditions: conditionsForLocalState,
          station: stationForLocalState,
          macroRegionOrigin: insightAlertStore.macroRegionOrigin ?? undefined,
          alertType: value.alertType,
          area: isInsightAlert ? undefined : value.area,
          locationId: finalLocationId,
          channels: value.channels as AlertChannel[],
          stationId: finalStationId,
          regionId: finalRegionId,
          operationRuleLocationItemId,
          sensorAlertConfig,
        });

        // Spec 043 — for insight alerts, route the save through the event
        // endpoint (PUT /v1/event/:id) instead of the single-row alert
        // endpoint. The event endpoint owns the multi-row reconcile logic
        // (T017) and can create/update/delete the rain + lightning rows
        // together as a single transaction. Standalone (non-insight) alerts
        // keep the original single-row path.
        if (isInsightAlert && operationRuleLocationItemId) {
          const { eventService } = getEnv<RootEnv>();
          await eventService.updateEvent(operationRuleLocationItemId, {
            alertType: value.alertType,
            alert: {
              recipients: recipientProcessedForBackend.map((r) => ({
                userId: r.userId,
              })),
              notifications: [
                {
                  id: notificationPayload.id ?? 0,
                  title: notificationPayload.title,
                  description: notificationPayload.description,
                  type: "before",
                  minutesType: notificationPayload.minutesType ?? 0,
                  minAdvanceTime: notificationPayload.minAdvanceTime,
                  maxAdvanceTime: notificationPayload.maxAdvanceTime,
                  minimumEventDuration: notificationPayload.minimumEventDuration,
                  sendUpdates: notificationPayload.sendUpdates ?? true,
                  sendCancellations:
                    notificationPayload.sendCancellations ?? true,
                },
              ],
              channels: value.channels as AlertChannel[],
              alertType: value.alertType,
            },
            sensorAlertConfig: sensorAlertConfig ?? undefined,
          });
        } else {
          await insightAlertStore.updateOperationRuleAlertAPI();
        }
        resetInitialValues()

        // Spec 043 — clear gateway-flow pending state so the next edit loads
        // from the freshly-fetched server data instead of stale ids that
        // were left in the store during the gateway round-trip.
        insightAlertStore.setSelectedSensorIds([]);
        insightAlertStore.setLightningSelection(null);
        insightAlertStore.setOperationRuleAlert(null);

        // Refetch the listing so grouped rows reflect the new sibling row
        // ids created by the multi-row reconcile (rain + lightning).
        // Without this the listing's `_groupRowIds` stays stale and the next
        // edit would only load the originally-listed row.
        await insightAlertStore.getOperationRuleAlertForTableAPI({});
      }

      onClose();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [insightAlertStore, toast, t]
  );

  return {
    handleOnSaveFormAlertModal,
  };
}
